import React, { useState, useEffect } from "react";
import "./DashboardComponent.css";
import {
  refreshDashboard,
} from "../services/AnalyticsService";
import {
  Box,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleIcon from "@mui/icons-material/PeopleOutlined";
import MemoryIcon from "@mui/icons-material/Memory";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Bar } from "react-chartjs-2";
import { useTheme } from "@mui/material";
import axios from "axios";
import Configuration from "../environment";
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Legend,
  Title,
  Tooltip,
} from "chart.js";
import DateCriteria from "./common/DateCriteria";
import { useDispatch } from "react-redux";

ChartJS.register(
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Legend,
  Title,
  Tooltip
);

const KpiCard = ({ label, value, icon, tint, color }) => (
  <Card className="kpi-card">
    <CardContent>
      <div className="kpi-card-header">
        <span className="kpi-card-label">{label}</span>
        <div className="kpi-card-icon" style={{ background: tint }}>
          <div style={{ color }}>{icon}</div>
        </div>
      </div>
      <Typography className="kpi-card-value">
        {value === null || value === undefined ? "—" : value}
      </Typography>
    </CardContent>
  </Card>
);

const DashboardComponent = () => {
  const theme = useTheme();

  const data1 = {
    datasets: [
      {
        backgroundColor: "#E5484D",
        barPercentage: 0.5,
        barThickness: 12,
        borderRadius: 4,
        categoryPercentage: 0.5,
        data: [18, 5, 19, 27, 29, 19, 20],
        label: "Up",
        maxBarThickness: 10,
      },
      {
        backgroundColor: "#F3D6D4",
        barPercentage: 0.5,
        barThickness: 12,
        borderRadius: 4,
        categoryPercentage: 0.5,
        data: [11, 20, 12, 29, 30, 25, 13],
        label: "Down",
        maxBarThickness: 10,
      },
    ],
    labels: ["1 Aug", "2 Aug", "3 Aug", "4 Aug", "5 Aug", "6 Aug", "7 Aug"],
  };

  const options = {
    animation: false,
    layout: { padding: 0 },
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
      y: {
        beginAtZero: true,
        min: 0,
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          borderDash: [2],
          color: theme.palette.divider,
          drawBorder: false,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        enabled: true,
        footerColor: theme.palette.text.secondary,
        intersect: false,
        mode: "index",
        titleColor: theme.palette.text.primary,
      },
    },
  };

  const dispatcher = useDispatch();

  const handlePickedDate = (times) => {
    dispatcher({ type: "START_LOADING" });
    refreshDashboard(times.startDate, times.endDate)
      .then((resp) => {
        setActiveController(resp.controllerNumber);
        setActiveUsers(resp.activeUsers);
        setTotalUpTime(resp.totalUpTime);
        setCountActivePos(resp.countActivePos);
      })
      .finally(() => {
        dispatcher({ type: "STOP_LOADING" });
      });
  };

  const [totalUpTime, setTotalUpTime] = useState(null);
  const [activeControllers, setActiveController] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [countActivePos, setCountActivePos] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState(null);

  useEffect(() => {
    axios
      .get(Configuration.backend_api_url + "/controllers/health-status/notification", {
        headers: { authorization: localStorage.getItem("token") },
      })
      .then((resp) => setRecentAlerts(resp.data))
      .catch(() => setRecentAlerts({}));
  }, []);


  const defaultPeriod = 30  * 60 * 1000;
  const date = new Date()
  const defaultDates = [new Date(date.getTime() - defaultPeriod), date]

  useEffect(() => {
    dispatcher({ type: "START_LOADING" });

    refreshDashboard(defaultDates[0], defaultDates[1])
    .then((resp) => {
      setActiveController(resp.controllerNumber);
      setActiveUsers(resp.activeUsers);
      setTotalUpTime(resp.totalUpTime);
      setCountActivePos(resp.countActivePos);
    })
    .finally(() => {
      dispatcher({ type: "STOP_LOADING" });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Tableau de bord</h1>
          <p>Vue d'ensemble de l'activité de votre parc</p>
        </div>
        <DateCriteria pickedDates={handlePickedDate} startDate={defaultDates[0]} endDate={defaultDates[1]} />
      </div>

      <div className="kpi-grid">
        <KpiCard
          label="TEMPS DE FONCTIONNEMENT"
          value={totalUpTime === null ? null : `${Math.round(totalUpTime / 60)}h`}
          icon={<AccessTimeIcon />}
          tint="#FBE4E3"
          color="#E5484D"
        />
        <KpiCard
          label="CONTRÔLEURS ACTIFS"
          value={activeControllers}
          icon={<MemoryIcon />}
          tint="#E3F6EA"
          color="#1B8A4B"
        />
        <KpiCard
          label="UTILISATEURS ACTIFS"
          value={activeUsers}
          icon={<PeopleIcon />}
          tint="#FFF4E0"
          color="#B7791F"
        />
        <KpiCard
          label="POINTS DE VENTE ACTIFS"
          value={countActivePos}
          icon={<PlaceOutlinedIcon />}
          tint="#FBE4E3"
          color="#E5484D"
        />
      </div>

      <div className="dashboard-bottom-row">
        <Card className="chart-card">
          <CardContent>
            <div className="chart-card-header">
              <div>
                <div className="chart-card-title">Disponibilité des contrôleurs</div>
                <div className="chart-card-subtitle">Heures en ligne / hors ligne par jour</div>
              </div>
              <div className="chart-legend">
                <span><i style={{ background: "#E5484D" }}></i>En ligne</span>
                <span><i style={{ background: "#F3D6D4" }}></i>Hors ligne</span>
              </div>
            </div>
            <Box sx={{ height: 220, position: "relative", overflow: "hidden" }}>
              <Bar data={data1} options={options} />
            </Box>
          </CardContent>
        </Card>

        <Card className="activity-card">
          <CardContent>
            <div className="chart-card-title" style={{ marginBottom: 12 }}>Activité récente</div>
            {recentAlerts === null ? (
              <div className="activity-empty">Chargement…</div>
            ) : Object.keys(recentAlerts).length === 0 ? (
              <div className="activity-empty">Aucune alerte récente</div>
            ) : (
              Object.keys(recentAlerts).map((name) => (
                <div className="activity-row" key={name}>
                  <div className="activity-row-icon">
                    <WarningAmberIcon fontSize="small" />
                  </div>
                  <div className="activity-row-text">
                    <div className="activity-row-title">{name}</div>
                    <div className="activity-row-sub">Seuil dépassé : {recentAlerts[name]}</div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardComponent;
